import type { InsuranceVerdict } from '@/hooks/useInsuranceVerdicts'
import { useMemo } from 'react'
import { HoverCard } from '@mantine/core'
import { usePreviewPopover } from '../hooks/usePreviewPopover'
import {
  getVisibleDays,
  sameLocalDay,
  STRIP_HEIGHT,
  STRIP_HEIGHT_BIG,
  type AppointmentStatusValue,
  type CalendarEvent,
} from './scheduleCalendar.tokens'
import { useBigScreen } from '../hooks/useBigScreen'
import { EventStrip, type AppointmentContext } from './AppointmentBlocks'
import type { AppointmentSearchResult } from './appointment-search'

/** Appointments shown per day before collapsing into "+N more". */
const MAX_PILLS = 3
/** Day-cell floor: tall enough for MAX_PILLS strips plus the date header. On
 *  desktop Macs it grows with the strips so the month grid fills the same space
 *  the week grid does. */
const CELL_MIN_H = 140
const CELL_MIN_H_BIG = 180

export interface ScheduleMonthViewProps {
  /** Visible date range from FullCalendar's month view (start inclusive, end exclusive). */
  range: { start: Date; end: Date }
  events: CalendarEvent[]
  isStuffMember: boolean
  isCareCoordinator: boolean
  selectedPractitionerCount: number
  isSendingMagicLink: boolean
  insuranceVerdictMap?: Record<string, InsuranceVerdict>
  returningPatientMap?: Record<string, boolean>
  doctorNameById?: Record<string, string>
  onOpenCall: (encounterId: string) => void
  onReschedule: (appt: CalendarEvent) => void
  onReassign: (appt: CalendarEvent) => void
  onCancel: (encounterId: string) => void
  /** Passed to the preview so the confirmation dialog cannot dismiss it. */
  isCancelConfirmOpen?: boolean
  onStatusChange: (appointmentId: string, status: AppointmentStatusValue) => void
  onSendMagicLink: (encounterId: string) => void
  onCopyMagicLink: (encounterId: string) => void
  /** Refresh the clinic-decision map after a decision is saved in the preview. */
  onClinicDecisionChange?: () => void
  onAppointmentTransferred?: () => void
  onJumpToAppointment?: (result: AppointmentSearchResult) => void
  highlightedEventId?: string | null
  onSelectDay: (date: Date) => void
}

const isToday = (d: Date) => sameLocalDay(d, new Date())

/**
 * Custom v2-style month grid (Mon–Fri, weekends hidden to match the rest of the
 * schedule). Appointments render as the same white event strips used in the week
 * grid (EventStrip) — a left doctor-colored border, title, status/insurance
 * icons, time, a 3-dots actions menu and the rich detail preview. Days with more
 * than MAX_PILLS collapse into a "+N more" that opens the day view; out-of-month
 * days are dimmed.
 */
export const ScheduleMonthView = ({
  range,
  events,
  isStuffMember,
  isCareCoordinator,
  selectedPractitionerCount,
  isSendingMagicLink,
  insuranceVerdictMap,
  returningPatientMap,
  doctorNameById,
  onOpenCall,
  onReschedule,
  onReassign,
  onCancel,
  isCancelConfirmOpen,
  onStatusChange,
  onSendMagicLink,
  onCopyMagicLink,
  onClinicDecisionChange,
  onAppointmentTransferred,
  onJumpToAppointment,
  highlightedEventId,
  onSelectDay,
}: ScheduleMonthViewProps) => {
  const preview = usePreviewPopover(events)
  // Desktop Macs get the taller strips / cells (see useBigScreen).
  const isBigScreen = useBigScreen()
  const stripH = isBigScreen ? STRIP_HEIGHT_BIG : STRIP_HEIGHT
  const cellMinH = isBigScreen ? CELL_MIN_H_BIG : CELL_MIN_H

  // Shared context handed to every appointment block — see AppointmentBlocks.tsx.
  const ctx: AppointmentContext = {
    isStuffMember,
    isCareCoordinator,
    selectedPractitionerCount,
    isSendingMagicLink,
    insuranceVerdictMap,
    returningPatientMap,
    doctorNameById,
    onOpenCall,
    onReschedule,
    onReassign,
    onCancel,
    isCancelConfirmOpen,
    onStatusChange,
    onSendMagicLink,
    onCopyMagicLink,
    onClinicDecisionChange,
    onAppointmentTransferred,
    onJumpToAppointment,
    highlightedEventId,
  }

  // Weekdays (Mon–Fri) across the visible grid.
  const days = useMemo(() => getVisibleDays(range), [range])

  // The target month = a date safely inside it (row 2 of the grid).
  const reference = useMemo(() => {
    const d = new Date(range.start)
    d.setDate(d.getDate() + 7)
    return { month: d.getMonth(), year: d.getFullYear() }
  }, [range.start])

  // Appointments grouped by local day key.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const ev of events) {
      if (ev.display === 'background' || !ev.start) continue
      const d = new Date(ev.start)
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      const list = map.get(key)
      if (list) list.push(ev)
      else map.set(key, [ev])
    }
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start))
    return map
  }, [events])

  const weekdayLabels = days
    .slice(0, 5)
    .map((d) => d.toLocaleDateString(undefined, { weekday: 'short' }))

  return (
    <div className="mt-3 overflow-hidden rounded-[14px] border border-[#EAECEF] bg-white">
      <div className="grid grid-cols-5 bg-[#F7F8FA]">
        {weekdayLabels.map((label, i) => (
          <div
            key={`${label}-${i}`}
            className="border-l border-[#EAECEF] py-2.5 text-center text-[13px] font-semibold text-[#8A8F98] [&:nth-child(5n+1)]:border-l-0"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-5">
        {days.map((day, i) => {
          const dimmed = day.getMonth() !== reference.month || day.getFullYear() !== reference.year
          const today = isToday(day)
          const dayAppts =
            byDay.get(`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`) ?? []
          const visible = dayAppts.slice(0, MAX_PILLS)
          const hidden = dayAppts.slice(MAX_PILLS)
          const overflow = hidden.length
          // Open popovers toward the grid interior so they don't clip at the
          // right edge on the last two columns.
          const position = i % 5 >= 3 ? 'bottom-end' : 'bottom-start'
          const previewSide = i % 5 >= 3 ? 'left-start' : 'right-start'

          return (
            <div
              key={day.toISOString()}
              className="flex flex-col border-t border-l border-[#EAECEF] p-1.5 [&:nth-child(5n+1)]:border-l-0"
              style={{
                minHeight: cellMinH,
                background: today ? 'rgba(45,116,224,.045)' : undefined,
              }}
            >
              <div className="mb-1 flex items-center">
                <button
                  type="button"
                  onClick={() => onSelectDay(day)}
                  title="Open day view"
                  className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-[12.5px] font-bold ${
                    today
                      ? 'bg-[#2D74E0] text-white'
                      : dimmed
                        ? 'text-[#C0C4CB] hover:bg-[#F1F2F4]'
                        : 'text-[#1F2733] hover:bg-[#EAF1FE]'
                  }`}
                >
                  {day.getDate()}
                </button>
              </div>

              <div className={`flex min-h-0 flex-1 flex-col gap-1 ${dimmed ? 'opacity-50' : ''}`}>
                {visible.map((ev) => (
                  <div key={ev.id} style={{ height: stripH }}>
                    <EventStrip
                      appt={ev}
                      ctx={ctx}
                      preview={preview}
                      previewPosition={previewSide}
                      big={isBigScreen}
                    />
                  </div>
                ))}
                {overflow > 0 && (
                  <HoverCard
                    position={position}
                    width={240}
                    withinPortal
                    radius={12}
                    openDelay={60}
                    closeDelay={180}
                    styles={{
                      dropdown: {
                        padding: 6,
                        border: '1px solid #E4E7EB',
                        boxShadow: '0 14px 36px rgba(16,24,40,.2)',
                      },
                    }}
                  >
                    <HoverCard.Target>
                      <div className="cursor-pointer rounded px-1.5 py-0.5 text-[11px] font-semibold text-[#5A616B] hover:bg-[#F1F2F4]">
                        +{overflow} more
                      </div>
                    </HoverCard.Target>
                    <HoverCard.Dropdown>
                      <div className="px-[9px] pt-[7px] pb-2 text-[11px] font-bold tracking-[.4px] text-[#9AA0A8] uppercase">
                        {day.toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                      <div className="flex flex-col gap-[5px]">
                        {hidden.map((m) => (
                          <div key={m.id} style={{ height: stripH }}>
                            <EventStrip
                              appt={m}
                              ctx={ctx}
                              preview={preview}
                              previewPosition={previewSide}
                              previewWithinPortal={false}
                              big={isBigScreen}
                            />
                          </div>
                        ))}
                      </div>
                    </HoverCard.Dropdown>
                  </HoverCard>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
